import { OutlineElement } from '@phase2/outline-core';
import { html, TemplateResult, CSSResultArray } from 'lit';
import { customElement, query, property } from 'lit/decorators.js';
import Splide from '@splidejs/splide';

import componentStyles from './pc-carousel.css.lit';
import headingStyle from '../pc-heading/pc-heading.lightDom.css.lit';

/**
 * @extends LitElement
 * @element pc-carousel
 *
 * @property {boolean} full-bleed - Whether the carousel should be full-bleed.
 * @property {boolean} autoplay - Whether the carousel should autoplay.
 * @property {boolean} slide-without-image - Whether the carousel should have a slide without an image.
 * @property {boolean} slide-with-image - Whether the carousel should have a slide with an image.
 *
 * @slot - The carousel content.
 *
 */

import '../pc-container/pc-container';
@customElement('pc-carousel')
export class PCCarousel extends OutlineElement {
  static styles: CSSResultArray = [componentStyles, headingStyle];

  _splide: Splide;

  @query('#splide')
  _splideRef: HTMLElement;

  slideWithoutImage = false;

  @property({ type: Boolean, reflect: true, attribute: 'full-bleed' })
  fullBleed: boolean;

  @property({ type: Boolean })
  autoplay = false;

  @query('slot')
  _slot: HTMLSlotElement;

  slides: TemplateResult[] = [];

  createSplide() {
    if (!this._splideRef) {
      throw new Error('Splide reference is null');
    }

    const usePagination: boolean = this.slides.length > 1;

    this._splide = new Splide(this._splideRef, {
      speed: 500,
      perPage: 1,
      perMove: 1,
      gap: 300,
      arrows: false,
      pagination: usePagination,
      paginationKeyboard: usePagination,
      autoplay: this.autoplay,
      autoHeight: true,
    }).mount();

    // Add event listener for slide changes
    this._splide.on('moved', () => {
      const activeSlide = this._splideRef.querySelector(
        '.splide__slide.is-active'
      );
      const hasImage = activeSlide?.querySelector('img') !== null;

      this._splideRef.classList.remove(
        'carousel--slide-with-image',
        'carousel--slide-without-image'
      );
      this._splideRef.classList.add(
        hasImage
          ? 'carousel--slide-with-image'
          : 'carousel--slide-without-image'
      );
    });

    // Set initial class based on first slide
    const firstSlide = this._splideRef.querySelector(
      '.splide__slide.is-active'
    );
    const hasImage = firstSlide?.querySelector('img') !== null;
    this._splideRef.classList.add(
      hasImage ? 'carousel--slide-with-image' : 'carousel--slide-without-image'
    );
  }

  firstUpdated() {
    if (!this._slot) {
      throw new Error('Slot is null');
    }
    this.slides = this._slot
      .assignedElements()
      .map(item => html` <li class="splide__slide" role="">${item}</li> `);
    setTimeout(() => {
      this.createSplide();
      this.deleteAriaRoleFromSlide();
    }, 0);
    this.requestUpdate();
  }

  // Removes unnecessary 'role="group"' attributes from slides that SplideJS adds,
  // as they can cause accessibility issues according to axe DevTools.
  deleteAriaRoleFromSlide() {
    const listItems = this.renderRoot?.querySelectorAll('.splide__slide');
    if (listItems.length > 0)
      Array.from(listItems).forEach(listItem =>
        listItem.removeAttribute('role')
      );
  }

  carouselTemplate() {
    return this.fullBleed
      ? html`
          <div class="splide" id="splide">
            <div class="splide__track">
              <ul class="splide__list ">
                <slot></slot>
                ${this.slides}
              </ul>
            </div>
          </div>
        `
      : html`
          <pc-container>
            <div class="splide" id="splide">
              <div class="splide__track">
                <ul class="splide__list ">
                  <slot></slot>
                  ${this.slides}
                </ul>
              </div>
            </div>
          </pc-container>
        `;
  }

  render(): TemplateResult {
    return html` <div class="carousel">${this.carouselTemplate()}</div> `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'pc-carousel': PCCarousel;
  }
}
